/**
 * Centralized validation, success, and confirmation messages per spec (Validations 3.txt).
 * Use these constants for consistent wording across Site, Onboarding, Visit, Report, and CRF modules.
 */

// Common (e.g. post–e-sign success message for all authenticate transactions)
export const common = {
  authSuccess: 'Saved Successfully.',
};

// Site Proposed & Selection
export const sites = {
  investigatorRequired: 'Please select Investigator.',
  siteNameRequired: 'Please enter Site Name.',  
  siteNameMinLength: 'Site Name must be at least 3 characters.',
  siteCodeRequired: 'Please enter Site Code.',
  countryRequired: 'Please select Country.',
  recruitmentRequired: 'Please enter No. Of Recruitment Projections.',
  recruitmentInvalidNumber: 'Please enter valid Number.',
  recruitmentNegative: 'Value cannot be negative.',
  savedSuccess: 'Saved Successfully.',
  /** Site Feasibility: inactivate site blocked while scheduled visits are still active (API returns same text; toast only in UI). */
  cannotInactiveWithActiveVisits:
    'This site cannot be set inactive while it has active scheduled visits.',
  assignCRA: 'Please assign CRA.',
  emailInvalid: 'Please enter valid Email Address.',
  /** Add New Site: must change something from the initial / pre-filled state before save (e-sign). */
  atLeastOneChangeRequired: 'Please update at least one value.',
  // AI Suggested Sites
  aiSelectOneSite: 'Please select at least one Site from the list.',
  aiPiNotInSystem: 'The following PI name(s) are not registered in the Gateway.',
  aiRequiredFieldsBlank: 'Some selected sites have missing required data (PI Name, Site Name, or Country). Opening Add New Site with available data for you to complete.',
  aiSitesFilteredByPiName:
    'All returned sites were hidden because the PI field did not look like a person\'s name (roles, titles, or blank PI are excluded). Try Get AI Suggestions again or choose another country.',
  aiNoSitesForSelectedCountries:
    'No data found for the selected countries.',
  /** Shown when the external AI / integrated provider is unavailable or returns an error (hide technical API text). */
  aiIntegratedSourceUnavailable:
    'Unable to retrieve data from the integrated source system.',
  /** AI feature disabled or misconfigured in appsettings (replaces raw server text). */
  aiSuggestionNotEnabled:
    'AI site suggestions are not enabled. Please contact your administrator.',
  /** Model returned no usable content (replaces raw server text). */
  aiSuggestionEmptyResponse:
    'Service is currently unavailable. Please try again later.',
  /**
   * Fallback when the integrated source fails in a way that has no specific validation message
   * (replaces raw JSON, stack traces, or provider error bodies on the UI).
   */
  aiSuggestionUnableToComplete:
    'Unable to retrieve AI site suggestions. Please try again later.',
};

// Site Onboarding
export const onboarding = {
  selectOneSite: 'Please select at least one Site to Onboard.',
  siteCodeRequired: 'Please select Site',
  siteCodeDuplicate: 'Site already exists for this project.',
  frequencyRequired: 'Please select visit Frequency.',
  siteVisitsRequired: 'Please enter number of Site Visits.',
  siteVisitsMin: 'Site visits must be at least 1.',
  siteVisitsPositiveInteger: 'Please enter valid Number of Site Visits.',
  /** Message when visits exceed max for frequency. Use with .replace('{max}', n).replace('{frequency}', label) */
  siteVisitsMaxForFrequency: 'Site visits cannot exceed {max} for {frequency}.',
  /** API messageKey ssvIncomplete — full copy only here. */
  ssvIncomplete: 'All Site Selection Visits (SSV) must be completed before onboarding.',
  sivScheduledLock:
    'Site Onboarding is locked because Site Initiation Visit is already scheduled for this site.',
};

// Monitoring Visit & Report Management
export const visits = {
  siteRequired: 'Please select Site.',
  visitTypeRequired: 'Please select Visit Type.',
  visitTypeRequiredAlt: 'Please select Visit Type.',
  craRequired: 'Please assign CRA.',
  visitModeRequired: 'Please select Visit Mode.',
  startDateRequired: 'Please select Scheduled Start Date.',
  startDateRequiredAlt: 'Please select start date.',
  endDateRequired: 'Please select Scheduled End Date.',
  endDateRequiredAlt: 'Please select End Date.',
  startAfterEnd: 'Start Date cannot be after End Date.',
  endBeforeStart: 'End Date cannot be before Start Date.',
  visitDatePresentOrFuture: 'Visit date must be present date or future date.',
  projectStudyRequired: 'Please select a valid project. A matching study is required.',
  ssvAlreadyOnboarded: 'Site Selection Visit has already been onboarded for this site.',
  completeSsvOnboarding: 'Please complete SSV on-boarding',
  savedSuccess: 'Saved Successfully.',
  inactiveConfirm: 'Are you sure you want to cancel this visit?',
  letterBodyEmpty: 'Visit Letter Content cannot be empty.',
  letterBodyShort: 'Please provide complete Visit details.',
  letterPreviewEmpty: 'Letter Content is empty.',
  visitLetterTemplateNotConfigured: 'Visit letter template is not configured for this project. Add it in Study Configuration → Letter Templates.',
  followUpLetterTemplateNotConfigured: 'Follow-up letter template is not configured for this project. Add it in Study Configuration → Letter Templates.',
  noDataToSave: 'Visit Letter Content cannot be empty.',
  letterSubmitEmpty: 'Please enter Content in the Letter Body before saving.',
  confirmationModeRequired: 'Please select Confirmation Mode.',
  confirmedOnRequired: 'Please select Confirmation Sent On.',
  confirmedOnNotPast: 'Confirmation Sent On must be today or a future date.',
  confirmedOnBeforeScheduledVisit: 'Confirmation Sent On cannot be before the Scheduled Visit Date.',
  confirmedOnBeforeVisitLetter: 'Confirmation Sent On cannot be before the Generate Visit Letter date.',
  confirmationRecipientRequired: 'Please select Confirmation Informed to PM.',
  actualStartRequired: 'Please enter Actual Start Date.',
  actualStartFuture: 'Actual Start Date cannot be in the Future.',
  actualEndRequired: 'Please enter Actual End Date.',
  actualEndBeforeStart: 'Actual End Date cannot be before Actual Start Date.',
  actualEndFuture: 'Actual End Date cannot be in the Future.',
  executionNotesMaxLength: 'Notes cannot exceed 2000 characters.',
  /** Schedule New Visit: CRA has another active visit overlapping the selected date range (soft validation). */
  craOverlapScheduleConfirmTitle: 'Confirm schedule',
  craOverlapScheduleConfirmMessage:
    'CRA has already been assigned to another visit, would you like to continue anyway?',
};

// In Progress – Start Report (CRFForm)
export const report = {
  draftOneValue: 'Please enter at least one value before saving the draft.',
  noDataToSave: 'No Data to Save',
  printAfterDraftOrSubmit: 'Printing is allowed only after the Record is saved as Draft or Submitted.',
  allFieldsRequired: 'All fields are required',
  updateDcfGeneratedField: "Please Update all Pending Reviewer's Comments.",
  dcfAnswerRequired:
    "Please Update all Pending Reviewer's Comments.",
  /** Submit: each open Reviewer's Comment row requires the Answer field to be updated from the saved value (Comments optional). */
  dcfAnswerOrRemarkRequired: "Please Update all Pending Reviewer's Comments.",
  noTemplateAssigned: 'No Report template assigned for this visit. Please map a template in Report Management (Map Template to Visit).',
  templateMappingInactive: 'Template mapping is inactive. Please ensure an active template is mapped in Report Management (Map Template to Visit).',
  pdfGenerationFailed:
    'Could not generate the PDF. Please try again or contact support if the problem continues.',
  pdfGenerationEmptyPreview:
    'Visit Report preview is empty. Close and reopen the preview, then try again.',
  pdfGenerationBlocked:
    'PDF generation was blocked by the server security layer. Please contact your administrator.',
  pdfGenerationPayloadTooLarge:
    'The report content is too large for the server to accept. Please contact your administrator.',
  pdfGenerationTimeout:
    'PDF generation timed out. Please try again with a smaller report or contact support.',
  pdfGenerationServerError:
    'PDF generation failed on the server. Please try again or contact support.',
  pdfGenerationInvalidResponse:
    'The server did not return a valid PDF. Please try again or contact support.',
};

// First/Second Review & Reviewer's Comments
export const review = {
  passwordRequired: 'Please enter Password',
  remarksRequired: 'Please enter Remarks',
};

export const dcf = {
  remarksRequired: 'Please enter Remarks.',
  alreadyRaised:
    "A Reviewer's Comment is already raised on this Field. Resolve or close it at the appropriate review tier (and involve CRA only when released and applicable), then you can raise another.",
  /** Reviewer modal: Open tier-routed DCF at your lane — short hint when Remarks + Submit is available. */
  reviewerOpenResolveAtTierHint:
    "This comment is open for this review stage. Enter remarks and use Submit to resolve it at this tier.",
  /** Reviewer modal: Open at same review tier as visit but not yet batch-released — primary action is Submit All to CRA. */
  reviewerOpenReleaseBeforeCraResponse:
    "This comment is open at your review stage but has not been released to CRA yet. Use Submit All Reviewer's Comments to CRA (below or on the Visit Report). After release, CRA can respond only on fields their profile and visit stage allow; Associated or review-tier-only branches may still be completed at this tier.",
  /** Reviewer modal: Open, same lane, routed to a named review tier — no in-modal resolve (e.g. no approve permission). */
  reviewerOpenRoutedReviewTierReviewerRemit:
    "This Reviewer's Comment is open on a field routed to this review stage (for example an Associated branch). Resolve or close it at this tier when your profile allows, or release to CRA when applicable. You cannot raise another comment on this field until it is resolved and closed.",
  /** Reviewer modal: DCF batch tier does not match current visit stage (e.g. open Second Review comment while viewing First Review). */
  reviewerOpenWrongTierForUser:
    'This Reviewer\'s Comment is currently linked to the "{dcfStage}" stage. Please Resolve the visit at that stage.',
  /** Reviewer modal: Open, no explicit review-tier routing on the row (legacy / In Progress path). */
  reviewerOpenWaitingCra:
    "This Reviewer's Comment is open. Resolve or close it when your role allows; CRA may be involved after release to CRA where applicable. You cannot raise another comment on this field until the thread is resolved and closed.",
  /** Toast when reviewer retries Raise while Open/Resolved on the same field. */
  raiseBlockedActiveThread:
    "A Reviewer's Comment is already active on this field. Resolve or close it first, then you can raise another if allowed.",
  /** STS Auto Capture fields are system-populated (matches API DcfUserMessages). */
  raiseBlockedStsAutoCaptureField:
    "Reviewer's Comments cannot be raised on STS Auto Capture field.",
  /** PM on MedEx / Associated branch fields assigned for PM completion (matches API DcfUserMessages). */
  raiseBlockedPmOwnAssignedField:
    "Reviewer's Comments cannot be raised on fields assigned to the Project Manager for completion.",
  /** PM on Associated branch at the branch's assigned review stage (e.g. First Review / iBranchStageCode 301). */
  raiseBlockedPmOwnAssignedBranchField:
    "Reviewer's Comments cannot be raised for its own Assigned Field.",
  /** Reviewer has unreleased Open DCF on tier-assigned branch fields — block raise on any other field until resolved. */
  raiseBlockedPendingAssignedTierDcf:
    "Please update and resolve open Reviewer's Comments on fields assigned to your review stage before raising a Reviewer's Comment on another field.",
  remarksOrUpdateRequired: "Please Enter Reviewer's Comments.",
  /** Matches CRFStageService Approve/Reject when any detail is Open or Resolved (not yet Closed). */
  closeAllBeforeApproveReject: "Please close all Reviewer's Comments first.",
  closeBlockedNotResolved: "This Reviewer's Comment must be resolved before it can be closed.",
  closeBlockedNotRaiser: "Only the user who raised this Reviewer's Comment can close it.",
  closeBlockedWrongStage:
    'This Reviewer\'s Comment applies to review stage "{dcfStage}". Close it when you are working in that review lane (current: "{currentStage}").',
  /** CRA: DCF routed to another review tier (e.g. First Review) while the report is at a different stage. */
  craResponseWrongReviewStage:
    "This Reviewer's Comment applies to a different review stage. Responses can be added when the visit report reaches that stage.",
  /**
   * CRA modal in-place copy. Placeholders: {dcfStage} = DCF batch stage, {currentStage} = visit header display stage.
   * Keep double quotes in the sentence so replace() is unambiguous for callers.
   */
  craResponseWrongReviewStageDetail:
    'This Reviewer\'s Comment is tied to review stage "{dcfStage}". The visit report is currently at "{currentStage}". You can add a response after the report reaches that review stage.',
  /** CRA: tier-routed (e.g. Associated branch) open DCF — vDCFResponse is for the reviewer at {stage}, not CRA. */
  craTierRoutedReviewerOnly:
    "Response remarks for this Reviewer's Comment are for the assigned review stage only. Update the answer on the visit report when permitted, or wait for the reviewer to resolve this thread.",
  craTierRoutedReviewerOnlyDetail:
    'This Reviewer\'s Comment is assigned to review stage "{stage}". Only a user working in that review lane can enter response remarks here. You may still update the answer field on the report and use Draft Save or Submit when your role allows.',
};

// Attribute & Template Management (CRF Management)
export const crf = {
  attributeGroupName: 'Please enter Report Group',
  attributeGroupDuplicate: 'Report Group with this name already exists.',
  remarksRequired: 'Please enter Remarks',
  
  attributeSubGroupName: 'Please enter Report Sub Group',
  attributeSubGroupDuplicate: 'Report Sub Group with this name already exists.',
  attributeDescription: 'Please enter Field Description',
  attributeDescriptionDuplicate: 'Field Description with this name already exists.',
  attributeGroupSelect: 'Please select Report Group',
  attributeSubGroupSelect: 'Please select Report Sub Group',
  attributeTypeSelect: 'Please select Report Type',
  attributeLabelValueRequired: 'Please enter Field Value.',
  attributeValuesInvalid: 'Please enter valid comma separated values.',
  activeRequired: 'Please select active status.',
  notNullRequired: 'Please select Not Null option.',
  templateNameRequired: 'Please enter Template Name',
  selectOneAttribute: 'Please select at least one attribute.',
  onlyDraftEditable: 'Only Draft Templates can be edited.',
  templateInUse: 'Template cannot be edited as it is already in Use.',
  templateAlreadyMapped: 'Template is already mapped to selected Visit.',
  inactiveTemplateCannotMap: 'Inactive CRF templates cannot be assigned to a visit. Please select an Active template.',
  deleteConfirm: 'Are you sure you want to delete this Template?',
  deleteInUse: 'Template cannot be deleted as it is already used in a transaction.',
  templateDeleted: 'Saved Successfully.',
  savedSuccess: 'Saved Successfully.',
  savedSuccessLower: 'Saved Successfully.',
  cannotRemoveMapping:
    'Mapped Template cannot be removed as it is already in use.',
};

// Create Action Item (Monitoring Visit & Report Management)
export const actionItems = {
  visitTypeRequired: 'Please select Visit Type',
  visitTypeRequiresSite: 'Please select Site before selecting Visit Type.',
  siteRequired: 'Please select Site',
  assignActionItemToRequired: 'Please select Assign Issue',
  documentSectionsRequired: 'Please select Document Sections',
  sectionAttributesRequired: 'Please select Field Name',
  actionItemDescriptionRequired: 'Please enter Issue Description',
  assignedToRequired: 'Please select Assignee',
  dueDateRequired: 'Please select date',
  dueDateNoPast: 'Expected Closure Date cannot be in the past.',
  dateOfAwarenessRequired: 'Please select Date of Awareness',
  dateOfAwarenessNoFuture: 'Date of Awareness cannot be in the future.',
  dateOfIssueRequired: 'Please select Date of Issue Creation',
  dateOfIssueMustBeToday: 'Date of Issue Creation must be today.',
  issueCategoryRequired: 'Please select Issue Category',
  issueCategoryOtherRequired: 'Please enter Issue Category.',
  priorityRequired: 'Please select Priority',
  visitRequired: 'Please select Visit.',
  savedSuccess: 'Saved Successfully.',
  savesSuccess: 'Saved Successfully.', // alias for create action item success
  actionItemAlreadyExistsForField: 'An open issue already exists for this field. Close it before creating another.',
  crfHeaderDetailRequired: 'CRF header and detail are required to save this issue. Please open the issue from the Visit report row or ensure a valid question is selected.',
  pleaseDraftSaveFirst: 'Please do the Draft Save First.',
  cannotCreateIssueNoPermission:
    'Your profile is not permitted to create issues for this project.',
  replyRemarkRequired: 'Description of Action is required.',
  remarkSavedSuccess: 'Saved Successfully.',
  issueSubmittedToCreatorSuccess: 'Saved Successfully.',
  issueReopenedSuccess: 'Issue reopened.',
  /** Issue Review: Status intent must be Open before Re Open. */
  reopenCheckboxRequired: 'Please set Status to Open before reopening this issue.',
  /** Issue Review: Status intent must be Close before Close Issue. */
  closeIssueClearReopenCheckbox: 'Please set Status to Close before closing this issue.',
  closingDateRequired: 'Please select Closing Date.',
  closingDateBeforeIssue: 'Closing Date cannot be earlier than the Date of Issue Creation.',
  closingDateFuture: 'Closing Date cannot be after today.',
};

// STS Auto Capture (CRF visit report)
export const stsAutoCapture = {
  dataTooLarge: 'AutoCapture data size is too large.',
  loadFailed: 'AutoCapture failed. Please try again.',
};

export default {
  sites,
  onboarding,
  visits,
  report,
  review,
  dcf,
  crf,
  actionItems,
  stsAutoCapture,
};
